/**
 * Timer Manager - Centralized timer tracking and management
 *
 * Provides explicit naming, debugging capabilities, and automatic cleanup
 * for setTimeout operations across the application.
 * Uses web workers to ensure timers fire accurately even when main thread is busy.
 */

import logger from '../logger';
import WorkerBuilder from '../workers/worker-builder';
import timerWorker from './timer-worker';

/**
 * TIMER SPEED MODIFIER - For local development/testing
 *
 * Set to a value > 1 to speed up all timers (e.g., 2 = 2x faster, 10 = 10x faster)
 * Only applies when running in development mode
 *
 * Example: TIMER_SPEED_MODIFIER = 5 means a 10000ms timer becomes 2000ms
 */
let TIMER_SPEED_MODIFIER = 1;

/**
 * Set the timer speed modifier (1 = normal speed, 2 = 2x faster, etc.)
 * Only affects timers in development mode
 */
export const setTimerSpeedModifier = (modifier: number): void => {
  if (modifier > 0) {
    TIMER_SPEED_MODIFIER = modifier;
    logger.info(`Timer speed modifier set to: ${modifier}x`);
  }
};

/**
 * Get the current timer speed modifier
 */
export const getTimerSpeedModifier = (): number => {
  return TIMER_SPEED_MODIFIER;
};

/**
 * Apply speed modifier to delay if running locally
 */
const getModifiedDelay = (delay: number): number => {
  if (process.env.NODE_ENV === 'development' && TIMER_SPEED_MODIFIER > 1) {
    return Math.max(0, Math.round(delay / TIMER_SPEED_MODIFIER));
  }
  return delay;
};

export interface TimerInfo {
  uid: string;
  key: string;
  delay: number;
  description: string;
  createdAt: number;
  callback: () => void;
}

export interface TimerRegistry {
  set: (key: string, callback: () => void, delay: number, description?: string) => string;
  clear: (key: string) => boolean;
  clearAll: () => void;
  has: (key: string) => boolean;
  keys: () => string[];
  info: () => Array<{
    key: string;
    description: string;
    delay: number;
    age: number;
  }>;
}

/**
 * Create a new timer registry with namespaced tracking
 *
 * @param namespace - Name for debugging (e.g., 'BatchStore', 'PacSimulator')
 * @returns Timer management functions
 *
 * @example
 * ```js
 * const timers = createTimerRegistry('MyStore');
 *
 * // Set a timer
 * timers.set('my-timer', () => logger.log('Done'), 1000, 'Example timer');
 *
 * // Clear a timer
 * timers.clear('my-timer');
 *
 * // Debug all active timers
 * console.table(timers.info());
 * ```
 */
export function createTimerRegistry(namespace: string = 'App', debugEnabled: boolean = false): TimerRegistry {
  const timers = new Map<string, TimerInfo>();
  const worker: Worker = new WorkerBuilder(timerWorker) as Worker;

  const log = (message: string, data?: any): void => {
    if (debugEnabled) {
      logger.info(`[TimerRegistry:${namespace}] ${message}`, data || '');
    }
  };

  // Initialize worker message handler
  worker.onmessage = (message: MessageEvent): void => {
    let data: { uid: string } | null = null;

    try {
      data = JSON.parse(message.data);
    } catch (e) {
      console.error(`[TimerRegistry:${namespace}] Could not parse worker message`, e);
    }

    if (data && data.uid) {
      const timer = timers.get(data.uid);
      if (timer && typeof timer.callback === 'function') {
        log(`Timer executed: ${timer.key}`, { description: timer.description });
        timers.delete(data.uid);
        timer.callback();
      }
    }
  };

  return {
    /**
     * Set a named timer
     * Automatically clears any existing timer with the same key
     *
     * @param key - Unique identifier for this timer
     * @param callback - Function to execute when timer fires
     * @param delay - Delay in milliseconds
     * @param description - Optional description for debugging
     * @returns The timer key
     */
    set: (key: string, callback: () => void, delay: number, description: string = ''): string => {
      // Auto-clear existing timer with same key
      if (timers.has(key)) {
        const existing = timers.get(key);
        if (existing) {
          log(`Replacing existing timer: ${key}`);
          // Cancel the timer in the worker
          worker.postMessage(
            JSON.stringify({
              uid: existing.uid,
              type: 'clearTimeout',
            }),
          );
          timers.delete(key);
        }
      }

      // Generate unique ID for this timer
      const uid = key;

      const timerInfo: TimerInfo = {
        uid,
        key,
        delay,
        description,
        createdAt: Date.now(),
        callback,
      };

      timers.set(key, timerInfo);

      // Post message to worker to start the timer (with speed modifier applied if local)
      const modifiedDelay = getModifiedDelay(delay);
      worker.postMessage(
        JSON.stringify({
          uid,
          type: 'setTimeout',
          delay: modifiedDelay,
        }),
      );

      log(`Timer set: ${key}`, { delay, description });
      return key;
    },

    /**
     * Clear a specific timer by key
     *
     * @param key - Timer identifier
     * @returns true if timer was found and cleared, false otherwise
     */
    clear: (key: string): boolean => {
      const timer = timers.get(key);
      if (timer) {
        // Cancel the timer in the worker
        worker.postMessage(
          JSON.stringify({
            uid: timer.uid,
            type: 'clearTimeout',
          }),
        );
        timers.delete(key);
        log(`Timer cleared: ${key}`);
        return true;
      }
      return false;
    },

    /**
     * Clear all active timers in this registry
     * Useful for cleanup when disposing a store
     */
    clearAll: (): void => {
      log(`Clearing all ${timers.size} timers`);

      // Cancel all timers in the worker
      timers.forEach((timer) => {
        worker.postMessage(
          JSON.stringify({
            uid: timer.uid,
            type: 'clearTimeout',
          }),
        );
      });

      timers.clear();
    },

    /**
     * Check if a timer with the given key exists and is active
     *
     * @param key - Timer identifier
     * @returns true if timer exists
     */
    has: (key: string): boolean => {
      return timers.has(key);
    },

    /**
     * Get all active timer keys
     *
     * @returns Array of timer keys
     */
    keys: (): string[] => {
      return Array.from(timers.keys());
    },

    /**
     * Get detailed information about all active timers
     * Useful for debugging
     *
     * @returns Array of timer information objects
     */
    info: () => {
      return Array.from(timers.values()).map((t) => ({
        key: t.key,
        description: t.description,
        delay: t.delay,
        age: Date.now() - t.createdAt,
      }));
    },
  };
}

/**
 * Timer key builders for consistent naming across the application
 *
 * @example
 * ```js
 * timers.set(
 *   timerKey.batchMaterial(123),
 *   () => this.batchMaterial(data),
 *   2000
 * );
 * ```
 */
export const timerKey = {
  // Batch-related timers
  batch: (id: string | number) => `batch:${id}`,
  batchMaterial: (materialId: string | number) => `batch:material:${materialId}`,
  batchMetered: (id: string | number) => `batch:metered:${id}`,
  batchAction: () => 'batch:action',
  checkFinalWeight: (materialId: string | number) => `batch:check-weight:${materialId}`,
  continuousPress: (binId: string | number) => `batch:continuous-press:${binId}`,
  startBatch: () => 'batch:start',
  finishBatch: () => 'batch:finish',
  nextStep: () => 'batch:next-step',
  checkDataInScaleQueue: () => 'batch:check-data-in-scale-queue',
  checkAdmixturesFinish: () => 'batch:check-admixtures-finish',
  jog: (scaleId: string | number) => `batch:jog:${scaleId}`,
  jogUpdate: (scaleId: string | number) => `batch:jog-update:${scaleId}`,
  mixerFinishDelay: (mixerId: string | number) => `batch:mixer:${mixerId}`,
  mixerNoseplug: (mixerId: string | number) => `batch:mixer:noseplug:${mixerId}`,

  // Pump-related timers
  pump: (pumpId: string | number) => `pump:${pumpId}`,
  pumpMeter: (pumpId: string | number) => `pump:meter:${pumpId}`,
  pumpInventory: (pumpId: string | number) => `pump:inventory:${pumpId}`,
  pumpButtonRelease: (pumpId: string | number) => `pump:button-release:${pumpId}`,
  metered: (pumpId: string | number) => `pump:metered:${pumpId}`,
  setMeterSent: (pumpId: string | number) => `pump:set-meter-sent:${pumpId}`,

  // Scale-related timers
  scale: (scaleId: string | number) => `scale:${scaleId}`,
  scaleWeight: (scaleId: string | number) => `scale:weight:${scaleId}`,
  secondGateOpen: (scaleId: string | number) => `scale:second-gate-open:${scaleId}`,
  initialAdjustDelay: (scaleId: string | number) => `scale:initial-adjust-delay:${scaleId}`,

  // Tank-related timers
  tank: (tankId: string | number) => `tank:${tankId}`,
  tankEmpty: (tankId: string | number) => `tank:empty:${tankId}`,
  tankDischarge: (pumpId: string | number) => `tank:discharge:${pumpId}`,
  tankDischargeDelay: (pumpId: string | number) => `tank:discharge-delay:${pumpId}`,

  // Discharge-related timers
  discharge: () => 'discharge:main',
  dischargeDelay: (scaleId: number) => `discharge:delay:${scaleId}`,
  dischargeFinish: (scaleId: number) => `discharge:finish:${scaleId}`,
  dischargeToNextScale: (scaleId: number) => `discharge:to-next-scale:${scaleId}`,
  dischargeToNextScaleDelay: (scaleId: string | number) => `discharge:to-next-scale-delay:${scaleId}`,
  dischargeToNextScaleTimer: () => 'discharge:to-next-scale-timer',
  dischargeWater: () => 'discharge:water',
  dischargeAuthorization: () => 'discharge:authorization',
  goToDischarge: () => 'discharge:go-to',

  // Belt-related timers
  belt: (beltId: string | number) => `belt:${beltId}`,
  beltResume: (beltId: string | number) => `belt:resume:${beltId}`,
  beltsRunning: () => 'belts:running',

  // Device-related timers
  device: (deviceId: string | number) => `device:${deviceId}`,
  analog: (channelId: string | number) => `analog:${channelId}`,
  port: (portNum: string | number) => `port:${portNum}`,
  overrideDevice: (deviceId: string | number) => `device:override-device:${deviceId}`,

  // System timers
  version: () => 'system:version',
  pac: () => 'system:pac',
  pacInitial: () => 'system:pac:initial',
  ticks: () => 'system:ticks',
  websocket: () => 'system:websocket',
  websocketClose: () => 'system:websocket:close',
  observeBatchData: () => 'system:observe-batch-data',
  finishReplayAndReload: () => 'system:finish-replay-and-reload',
  finishReplay: () => 'system:finish-replay',
  remoteUser: () => 'system:remote-user',
  moistureProbe: () => 'system:moisture-probe',
  override: () => 'system:override',
  indicatorError: () => 'system:indicator-error',
  getStatus: () => 'system:get-status',
  demo: () => 'system:demo',
  rampUp: () => 'system:ramp-up',

  // Custom key builder for one-off cases
  custom: (category: string, ...parts: Array<string | number>): string => {
    return `${category}:${parts.join(':')}`;
  },
} as const;

/**
 * Debug helper - log all active timers from a registry
 *
 * @param registry - Timer registry to debug
 * @param name - Optional name for the output
 */
export function debugTimers(registry: TimerRegistry, name?: string): void {
  const info = registry.info();
  if (info.length === 0) {
    logger.info(`[${name || 'TimerRegistry'}] No active timers`);
    return;
  }

  console.group(`[${name || 'TimerRegistry'}] ${info.length} active timer(s)`);
  console.table(info);
  console.groupEnd();
}

/**
 * Debug helper - log all active timers from multiple registries
 *
 * @param registries - Array of { name, registry } objects
 *
 * @example
 * ```js
 * debugAllTimers([
 *   { name: 'BatchStore', registry: batchStore.timers },
 *   { name: 'PacSimulator', registry: pacSimulator.timers },
 * ]);
 * ```
 */
export function debugAllTimers(
  registries: Array<{ name: string; registry: TimerRegistry }>,
): void {
  let totalTimers = 0;

  registries.forEach(({ name, registry }) => {
    const info = registry.info();
    totalTimers += info.length;

    if (info.length > 0) {
      logger.group(`${name} - ${info.length} active timer(s)`);
      logger.table(info);
      logger.groupEnd();
    }
  });

  logger.info(`\nTotal active timers across all stores: ${totalTimers}`);
}
